export type AtlasModelStatus = 'source-backed-3d' | 'source-linked-3d' | 'group-3d' | 'mixed-stand'
export type AtlasAnatomyPart = 'blade-tip' | 'blade' | 'collar' | 'node' | 'runner' | 'roots' | 'seedhead'

export type AtlasBladeClass = 'needle' | 'fine' | 'medium' | 'broad' | 'mixed'
export type AtlasBladeTip = 'boat' | 'pointed' | 'blunt' | 'mixed'
export type AtlasGrowthForm = 'bunch' | 'rhizome' | 'stolon' | 'both' | 'bunch-rhizome' | 'mixed'
export type AtlasSeedheadForm =
  | 'panicle'
  | 'spike'
  | 'awned-spike'
  | 'digitate'
  | 'raceme'
  | 'y-fork'
  | 'hidden'
  | 'buffalo'
  | 'mixed'

export interface AtlasMorphologySource {
  label: string
  publisher: string
  href: string
}

export interface AtlasMorphologyProfile {
  scientificName: string
  kind: 'species' | 'species-group' | 'mixture'
  status: AtlasModelStatus
  reviewLabel: string
  reviewNote: string
  fingerprint: [string, string, string]
  vernation: string
  bladeTipCue: string
  collarCue: string
  nodeCue: string
  runnerCue: string
  rootCue: string
  visual: {
    blade: AtlasBladeClass
    tip: AtlasBladeTip
    growth: AtlasGrowthForm
    seedhead: AtlasSeedheadForm
    posture: 'upright' | 'arching' | 'prostrate' | 'tufted' | 'mixed'
    surface: 'smooth' | 'ridged' | 'glossy' | 'hairy' | 'mixed'
  }
  sources: AtlasMorphologySource[]
}

const PSU_COOL_SEASON_ID: AtlasMorphologySource = {
  publisher: 'Penn State Extension',
  label: 'The Cool-season Turfgrasses: Identification',
  href: 'https://extension.psu.edu/the-cool-season-turfgrasses-identification',
}

const TEXAS_TURFGRASSES: AtlasMorphologySource = {
  publisher: 'Texas A&M AgriLife',
  label: 'Turfgrass identification and selection',
  href: 'https://aggieturf.tamu.edu/texas-turfgrasses/',
}

const source = (slug: string, label: string): AtlasMorphologySource => ({
  publisher: 'Texas A&M AgriLife',
  label,
  href: `https://aggieturf.tamu.edu/texas-turfgrasses/${slug}/`,
})

export const GRASS_ATLAS_MORPHOLOGY: Record<string, AtlasMorphologyProfile> = {
  'kentucky-bluegrass': {
    scientificName: 'Poa pratensis',
    kind: 'species',
    status: 'source-backed-3d',
    reviewLabel: '3D teaching model',
    reviewNote: 'Diagnostic geometry is source-backed and intentionally enlarged to make the identifying parts easier to see.',
    fingerprint: ['Keeled, boat-shaped blade tip', 'Short membranous ligule; no auricles', 'Underground rhizomes form dense sod'],
    vernation: 'The newest leaf is folded in the shoot, with one dominant mid-vein and a dull underside.',
    bladeTipCue: 'The two sides meet in a keeled, boat-shaped tip, like the prow of a canoe.',
    collarCue: 'A short membranous ligule is present; auricles are absent.',
    nodeCue: 'Smooth sheaths rise from a compact crown; the blade is folded rather than rolled in the shoot.',
    runnerCue: 'No aboveground stolon should be present. New shoots travel on rhizomes below the soil.',
    rootCue: 'A fibrous root mass is connected by shallow underground rhizomes.',
    visual: { blade: 'fine', tip: 'boat', growth: 'rhizome', seedhead: 'panicle', posture: 'upright', surface: 'smooth' },
    sources: [PSU_COOL_SEASON_ID],
  },
  'tall-fescue': {
    scientificName: 'Schedonorus arundinaceus',
    kind: 'species',
    status: 'source-linked-3d',
    reviewLabel: '3D teaching model',
    reviewNote: 'The model shows published identification traits with key structures enlarged for learning.',
    fingerprint: ['Broad, pointed blade', 'Many equal-sized lengthwise veins', 'Primarily bunch-forming'],
    vernation: 'The newest leaf is rolled in the shoot; the upper blade shows many similarly sized veins.',
    bladeTipCue: 'The broad blade tapers to a pointed tip rather than closing into a boat shape.',
    collarCue: 'A short membranous ligule is present; auricles are absent.',
    nodeCue: 'Overlapping sheaths form a coarse, upright bunch with no long surface runners.',
    runnerCue: 'Tall fescue is primarily bunch-type. Some turf types can make short rhizomes, but not long visible stolons.',
    rootCue: 'A deep, dense fibrous root system sits below the bunch-forming crown.',
    visual: { blade: 'broad', tip: 'pointed', growth: 'bunch-rhizome', seedhead: 'panicle', posture: 'upright', surface: 'ridged' },
    sources: [PSU_COOL_SEASON_ID, source('tall-fescue', 'Tall fescue profile')],
  },
  'fine-fescue': {
    scientificName: 'Festuca spp.',
    kind: 'species-group',
    status: 'group-3d',
    reviewLabel: 'Fine fescue group model · not one species',
    reviewNote: 'Fine fescue names several related species. The specimen teaches shared traits and cannot distinguish every member of the group.',
    fingerprint: ['Needle-fine folded blades', 'Dull underside; veins hard to see', 'Tufted, sometimes weakly rhizomatous'],
    vernation: 'The newest leaf is folded; mature blades remain extremely narrow and often look bristle-like.',
    bladeTipCue: 'The narrow folded blade ends in a fine keeled point.',
    collarCue: 'A short membranous ligule is present; auricles are absent.',
    nodeCue: 'Fine leaves emerge in soft tufts from a compact crown.',
    runnerCue: 'Most fine fescues are bunch-forming; creeping red fescue can spread slowly with rhizomes.',
    rootCue: 'Fine fibrous roots support tufts; creeping red fescue may show slender rhizomes.',
    visual: { blade: 'needle', tip: 'boat', growth: 'bunch-rhizome', seedhead: 'panicle', posture: 'tufted', surface: 'smooth' },
    sources: [PSU_COOL_SEASON_ID],
  },
  'perennial-ryegrass': {
    scientificName: 'Lolium perenne',
    kind: 'species',
    status: 'source-linked-3d',
    reviewLabel: '3D teaching model',
    reviewNote: 'The model shows the folded shoot, glossy blade back, small auricles, bunch habit, and spike seedhead.',
    fingerprint: ['Glossy blade underside', 'Small rudimentary auricles', 'Flattened spike seedhead'],
    vernation: 'The newest leaf is folded. A dominant mid-vein and smaller parallel veins run through the blade.',
    bladeTipCue: 'The blade ends in a keeled point and is notably glossy underneath.',
    collarCue: 'The ligule is short and membranous; auricles are small and rudimentary.',
    nodeCue: 'Flattened shoots and glossy-backed leaves rise from a bunch-forming crown.',
    runnerCue: 'Perennial ryegrass makes tillers within a bunch, not rhizomes or stolons.',
    rootCue: 'The crown anchors a fibrous root system without connecting runners.',
    visual: { blade: 'fine', tip: 'boat', growth: 'bunch', seedhead: 'spike', posture: 'upright', surface: 'glossy' },
    sources: [PSU_COOL_SEASON_ID],
  },
  'annual-ryegrass': {
    scientificName: 'Lolium multiflorum',
    kind: 'species',
    status: 'source-linked-3d',
    reviewLabel: '3D teaching model',
    reviewNote: 'The model emphasizes the rolled shoot and clasping auricles that separate annual from perennial ryegrass; its spike is shown without unsupported awns.',
    fingerprint: ['Rolled shoot', 'Long clasping auricles', 'Awned spike seedhead'],
    vernation: 'Unlike perennial ryegrass, the newest leaf is rolled in the shoot.',
    bladeTipCue: 'The keeled blade is glossy underneath and usually coarser than perennial ryegrass.',
    collarCue: 'Long claw-like auricles clasp the sheath; the ligule is short and membranous.',
    nodeCue: 'Ribbed leaves rise from a loose bunch with no creeping stems.',
    runnerCue: 'Annual ryegrass is bunch-forming and does not make rhizomes or stolons.',
    rootCue: 'A temporary fibrous root system supports the winter annual plant.',
    visual: { blade: 'medium', tip: 'boat', growth: 'bunch', seedhead: 'spike', posture: 'upright', surface: 'glossy' },
    sources: [PSU_COOL_SEASON_ID, { publisher: 'Texas A&M AgriLife', label: 'Annual ryegrass identification', href: 'https://aggieturf.tamu.edu/turfgrass-weeds/annual-ryegrass/' }],
  },
  'mixed-cool-season-blend': {
    scientificName: 'Multiple cool-season species',
    kind: 'mixture',
    status: 'mixed-stand',
    reviewLabel: 'Example mixed stand · not one plant or fixed formula',
    reviewNote: 'A mixture cannot have one canonical plant. The specimen intentionally combines bluegrass, ryegrass, and fescue forms.',
    fingerprint: ['More than one blade width', 'Glossy and dull leaf backs together', 'Bunches interrupted by spreading turf'],
    vernation: 'Both folded and rolled shoots may be present because the lawn contains more than one species.',
    bladeTipCue: 'Look for multiple tip shapes in the same sample, such as boat-shaped bluegrass beside pointed fescue.',
    collarCue: 'Collar traits should disagree across sampled shoots. Photograph several plants separately.',
    nodeCue: 'Coarse clumps, glossy ryegrass shoots, and finer spreading bluegrass can occur in one small plug.',
    runnerCue: 'The stand may combine bunch-forming plants with bluegrass rhizomes; there is no single growth habit.',
    rootCue: 'Roots and crowns belong to several neighboring plants. Separate shoots before interpreting below-soil structures.',
    visual: { blade: 'mixed', tip: 'mixed', growth: 'mixed', seedhead: 'mixed', posture: 'mixed', surface: 'mixed' },
    sources: [PSU_COOL_SEASON_ID, { publisher: 'University of Maryland Extension', label: 'Grass seed mixtures and blends', href: 'https://extension.umd.edu/resource/grass-seed' }],
  },
  bermudagrass: {
    scientificName: 'Cynodon dactylon',
    kind: 'species',
    status: 'source-linked-3d',
    reviewLabel: '3D teaching model',
    reviewNote: 'The specimen encodes fine leaves, hair-fringe collar, dual runners, and the digitate seedhead without claiming cultivar-level geometry.',
    fingerprint: ['Fine wiry blades', 'Rhizomes and stolons', 'Finger-like seedhead branches'],
    vernation: 'The newest leaf is folded; leaves may be smooth or hairy depending on cultivar.',
    bladeTipCue: 'The narrow blade tapers to a fine point.',
    collarCue: 'A conspicuous fringe of hairs forms the ligule; auricles are absent.',
    nodeCue: 'Thin, wiry stolons root at distinct nodes and carry papery sheaths.',
    runnerCue: 'Bermudagrass spreads aggressively with both aboveground stolons and underground rhizomes.',
    rootCue: 'Fibrous roots descend from crowns and nodes while shallow rhizomes connect shoots below the soil.',
    visual: { blade: 'fine', tip: 'pointed', growth: 'both', seedhead: 'digitate', posture: 'prostrate', surface: 'smooth' },
    sources: [source('bermudagrass', 'Bermudagrass profile'), TEXAS_TURFGRASSES],
  },
  kikuyugrass: {
    scientificName: 'Cenchrus clandestinus',
    kind: 'species',
    status: 'source-linked-3d',
    reviewLabel: '3D teaching model',
    reviewNote: 'The specimen emphasizes thick flattened runners, hairy collars, and flowers held within the leaf sheath.',
    fingerprint: ['Thick flattened stolons', 'Long hair fringe at the collar', 'White flower filaments near the canopy'],
    vernation: 'Leaves can be rolled or folded in the bud and are light green, flat, and pointed.',
    bladeTipCue: 'A flat, moderately broad blade narrows to a pointed tip.',
    collarCue: 'The pale collar has a long, unequal fringe of hairs and no auricles.',
    nodeCue: 'Coarse flattened stolons have swollen nodes and brown sheaths; nodes readily root.',
    runnerCue: 'A dense network of stout stolons above ground and rhizomes below ground drives rapid spread.',
    rootCue: 'Roots emerge at stolon nodes while fleshy rhizomes persist beneath the soil.',
    visual: { blade: 'medium', tip: 'pointed', growth: 'both', seedhead: 'hidden', posture: 'prostrate', surface: 'hairy' },
    sources: [{ publisher: 'UC Statewide IPM Program', label: 'Kikuyugrass identification gallery', href: 'https://ipm.ucanr.edu/weeds-identification-gallery/kikuyugrass/' }],
  },
  zoysiagrass: {
    scientificName: 'Zoysia spp.',
    kind: 'species-group',
    status: 'group-3d',
    reviewLabel: 'Zoysiagrass group model · not one species',
    reviewNote: 'Zoysia species and cultivars vary widely. The specimen shows shared turf traits rather than one cultivar.',
    fingerprint: ['Stiff bristly leaves', 'Hairy upper blade surface', 'Rhizomes and stolons'],
    vernation: 'The newest leaf is rolled in the shoot.',
    bladeTipCue: 'The stiff leaf narrows to a sharp point; width varies substantially by Zoysia species.',
    collarCue: 'The ligule is a fringe of hairs; auricles are absent.',
    nodeCue: 'Short, stiff shoots rise from a dense mat connected above and below the soil.',
    runnerCue: 'Zoysiagrass spreads with both stolons and rhizomes, usually more slowly than bermudagrass.',
    rootCue: 'A dense fibrous root system is joined by underground rhizomes.',
    visual: { blade: 'medium', tip: 'pointed', growth: 'both', seedhead: 'spike', posture: 'upright', surface: 'hairy' },
    sources: [source('zoysiagrass', 'Zoysiagrass profile'), TEXAS_TURFGRASSES],
  },
  'st-augustinegrass': {
    scientificName: 'Stenotaphrum secundatum',
    kind: 'species',
    status: 'source-linked-3d',
    reviewLabel: '3D teaching model',
    reviewNote: 'The specimen encodes broad blunt leaves, thick surface stolons, and the embedded spike-like seedhead.',
    fingerprint: ['Very broad blunt-tipped blade', 'Thick flattened stolons', 'Spikelets embedded in a thick axis'],
    vernation: 'The newest leaf is folded; mature blades are flat and smooth on both surfaces.',
    bladeTipCue: 'The very broad blade ends bluntly rather than tapering to a fine point.',
    collarCue: 'The ligule is a fringe of hairs; auricles are absent.',
    nodeCue: 'Thick, flattened stolons carry widely spaced nodes and broad leaves.',
    runnerCue: 'St. Augustinegrass spreads laterally by prominent aboveground stolons, not rhizomes.',
    rootCue: 'Fibrous roots descend from the crown and from stolon nodes touching the soil.',
    visual: { blade: 'broad', tip: 'blunt', growth: 'stolon', seedhead: 'spike', posture: 'arching', surface: 'smooth' },
    sources: [source('st-augustinegrass', 'St. Augustinegrass profile'), TEXAS_TURFGRASSES],
  },
  centipede: {
    scientificName: 'Eremochloa ophiuroides',
    kind: 'species',
    status: 'source-linked-3d',
    reviewLabel: '3D teaching model',
    reviewNote: 'The specimen emphasizes a low, open mat, slender stolons, and a single spike-like raceme.',
    fingerprint: ['Low apple-green mat', 'Slender stolons', 'Single spike-like raceme'],
    vernation: 'The newest leaf is folded; mature blades are flat and smooth.',
    bladeTipCue: 'The blade tip is moderately pointed to blunt, not sharply needle-like.',
    collarCue: 'A membranous ligule carries short hairs; auricles are absent.',
    nodeCue: 'Slender stolons show alternating short leaves and root at their nodes.',
    runnerCue: 'Centipedegrass spreads laterally with aboveground stolons and no strong rhizome network.',
    rootCue: 'A relatively shallow fibrous root system forms beneath the low stolon mat.',
    visual: { blade: 'medium', tip: 'blunt', growth: 'stolon', seedhead: 'raceme', posture: 'prostrate', surface: 'smooth' },
    sources: [source('centipedegrass', 'Centipedegrass profile'), TEXAS_TURFGRASSES],
  },
  bahiagrass: {
    scientificName: 'Paspalum notatum',
    kind: 'species',
    status: 'source-linked-3d',
    reviewLabel: '3D teaching model',
    reviewNote: 'The specimen encodes a coarse open clump and the high-value V-shaped seedhead cue.',
    fingerprint: ['Coarse open clumps', 'Strong short rhizomes', 'Two-branched Y or V seedhead'],
    vernation: 'Coarse folded blades rise from a compressed crown.',
    bladeTipCue: 'The broad, tough blade tapers to a pointed tip.',
    collarCue: 'A short membranous ligule sits at the junction; auricles are absent.',
    nodeCue: 'Flattened basal sheaths form a coarse, open clump rather than a carpet.',
    runnerCue: 'Bahiagrass appears bunch-forming at the surface but can connect crowns with strong, short rhizomes.',
    rootCue: 'A deep fibrous root system and short rhizomes anchor the drought-tolerant clump.',
    visual: { blade: 'broad', tip: 'pointed', growth: 'bunch-rhizome', seedhead: 'y-fork', posture: 'tufted', surface: 'ridged' },
    sources: [{ publisher: 'Texas A&M AgriLife', label: 'Bahiagrass identification', href: 'https://aggieturf.tamu.edu/turfgrass-weeds/bahiagrass/' }],
  },
  buffalograss: {
    scientificName: 'Bouteloua dactyloides',
    kind: 'species',
    status: 'source-linked-3d',
    reviewLabel: '3D teaching model',
    reviewNote: 'The specimen teaches hairy curled leaves and stolons. Its flower state is illustrative and must not imply male and female flowers occur on one plant.',
    fingerprint: ['Fine gray-green curled blades', 'Hairy ridged leaf surfaces', 'Male and female flowers differ'],
    vernation: 'The newest leaf is rolled; mature leaves are fine, ridged, and hairy on both surfaces.',
    bladeTipCue: 'The fine blade tapers to a point and often curls as it dries.',
    collarCue: 'The ligule is a fringe of hairs; auricles are absent.',
    nodeCue: 'Slender stolons root at nodes and form an open, low-growing native turf.',
    runnerCue: 'Buffalograss spreads slowly by aboveground stolons.',
    rootCue: 'A deep, drought-adapted fibrous root system sits beneath the relatively open canopy.',
    visual: { blade: 'fine', tip: 'pointed', growth: 'stolon', seedhead: 'buffalo', posture: 'tufted', surface: 'hairy' },
    sources: [source('buffalograss', 'Buffalograss profile'), TEXAS_TURFGRASSES],
  },
}

export function getAtlasMorphology(slug: string): AtlasMorphologyProfile {
  return GRASS_ATLAS_MORPHOLOGY[slug] ?? GRASS_ATLAS_MORPHOLOGY['mixed-cool-season-blend']
}
